import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card, TextField } from '../ui'
import { Brand } from '../components/Layout'

function AuthShell({ title, subtitle, children, footer }: {
  title: string
  subtitle: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className="editorial stack" style={{ minHeight: '100dvh' }}>
      <header className="container" style={{ paddingBlock: '24px' }}>
        <Brand />
      </header>
      <main className="grow row center" style={{ padding: '24px', alignItems: 'flex-start' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <Card variant="outlined">
            <div className="stack gap-5">
              <div className="stack gap-2">
                <h1 className="headline-small">{title}</h1>
                <p className="body-medium text-muted">{subtitle}</p>
              </div>
              {children}
            </div>
          </Card>
          {footer && (
            <p className="body-medium text-muted" style={{ textAlign: 'center', marginTop: '20px' }}>
              {footer}
            </p>
          )}
        </div>
      </main>
    </div>
  )
}

const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1)

/**
 * Where an invite link actually lands. It didn't exist before — `{appUrl}/invite/{token}` was
 * built and emailed, but nothing in the router matched it, so every invited teammate hit a dead
 * page with no way to join. This is that missing page, covering both people it's used against:
 * someone who has never signed up (a short signup form, joining instead of creating a workspace)
 * and someone who already has an account elsewhere (accept with one click once signed in as the
 * invited address).
 */
export function AcceptInvite() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { token } = useParams({ strict: false }) as { token?: string }

  const session = useQuery({ queryKey: ['session'], queryFn: api.session })
  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.inviteDetails(token!),
    enabled: Boolean(token),
    retry: false,
  })

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [stage, setStage] = useState<'details' | 'code'>('details')
  const [code, setCode] = useState('')
  const methods = useQuery({ queryKey: ['auth-methods'], queryFn: api.authMethods, retry: false, staleTime: 60_000 })
  const emailCode = methods.data?.emailCode === true

  const join = useMutation({
    mutationFn: () =>
      api.signup({
        name,
        email: invite.data!.email,
        password,
        // Unused by the server once inviteToken resolves — no workspace is created — but the
        // field is still required by the signup schema, so the workspace being joined stands in.
        company: invite.data!.workspaceName,
        inviteToken: token!,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      navigate({ to: '/app' })
    },
  })

  // Where the deployment can send a code, joining is a code too: no password to invent for a
  // workspace someone has just been added to. The company is unused once the invitation token
  // resolves, exactly as with the password form above.
  const requestJoinCode = useMutation({
    mutationFn: () =>
      api.requestSignupCode({ name, email: invite.data!.email, company: invite.data!.workspaceName, inviteToken: token! }),
    onSuccess: () => {
      setCode('')
      setStage('code')
    },
  })
  const verifyJoin = useMutation({
    mutationFn: () =>
      api.verifySignup({ name, email: invite.data!.email, company: invite.data!.workspaceName, code, inviteToken: token! }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      navigate({ to: '/app' })
    },
  })

  const accept = useMutation({
    mutationFn: async () => {
      const result = await api.acceptInvite(token!)
      // Whoever is accepting may already own or belong to another workspace from their own
      // signup — without this, landing on /app after accepting could still show that one.
      await api.switchWorkspace(result.workspaceId)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      navigate({ to: '/app' })
    },
  })

  const joinError = (emailCode ? (stage === 'code' ? (verifyJoin.error ?? requestJoinCode.error) : requestJoinCode.error) : join.error) as
    | Error
    | null
  const fieldError = (field: string) => {
    if (!(joinError instanceof ApiError)) return undefined
    const issues = joinError.issues as { fieldErrors?: Record<string, string[]> } | undefined
    return issues?.fieldErrors?.[field]?.[0]
  }

  if (!token) {
    return (
      <AuthShell title="Invitation not found" subtitle="This link is missing its invitation code.">
        <Link to="/login">Sign in</Link>
      </AuthShell>
    )
  }

  if (invite.isPending || session.isPending || methods.isPending) {
    return <div className="skeleton" style={{ height: '100dvh' }} />
  }

  if (invite.isError) {
    return (
      <AuthShell
        title="Invitation not found"
        subtitle={
          invite.error instanceof ApiError
            ? invite.error.message
            : 'This invitation has expired, been revoked, or already been used.'
        }
      >
        <Button variant="outlined" full onClick={() => navigate({ to: '/login' })}>
          Sign in
        </Button>
      </AuthShell>
    )
  }

  const { email, role, workspaceName } = invite.data!
  const user = session.data?.user

  if (user) {
    const matches = user.email.toLowerCase() === email.toLowerCase()
    return (
      <AuthShell title={`Join ${workspaceName}`} subtitle={`You've been invited as ${roleLabel(role)}.`}>
        {matches ? (
          <div className="stack gap-4">
            <p className="body-medium text-muted">Signed in as {user.email}.</p>
            {accept.error && (
              <Banner tone="error">
                {accept.error instanceof ApiError || accept.error instanceof Error
                  ? accept.error.message
                  : 'That did not go through.'}
              </Banner>
            )}
            <Button full size="large" disabled={accept.isPending} onClick={() => accept.mutate()}>
              {accept.isPending ? 'Joining…' : `Accept and join ${workspaceName}`}
            </Button>
          </div>
        ) : (
          <div className="stack gap-4">
            <Banner tone="error">
              This invitation was sent to {email}. You're signed in as {user.email} — sign out and
              sign back in as {email} to accept it.
            </Banner>
            <Button
              variant="outlined"
              full
              onClick={async () => {
                await api.logout()
                await queryClient.invalidateQueries({ queryKey: ['session'] })
              }}
            >
              Sign out
            </Button>
          </div>
        )}
      </AuthShell>
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!emailCode) return join.mutate()
    if (stage === 'details') return requestJoinCode.mutate()
    verifyJoin.mutate()
  }
  const busy = join.isPending || requestJoinCode.isPending || verifyJoin.isPending

  return (
    <AuthShell
      title={`Join ${workspaceName}`}
      subtitle={
        emailCode
          ? stage === 'code'
            ? `Enter the six-digit code we sent to ${email}.`
            : `You've been invited as ${roleLabel(role)}. We'll email you a code to finish joining.`
          : `You've been invited as ${roleLabel(role)}. Set a password to finish joining.`
      }
      footer={
        <>
          Already have an account with this email?{' '}
          <Link to="/login">Sign in</Link>, then open this invitation link again.
        </>
      }
    >
      <form className="stack gap-4" onSubmit={submit} noValidate>
        {joinError && !fieldError('email') && <Banner tone="error">{joinError.message}</Banner>}
        <TextField label="Email" value={email} disabled readOnly />
        <TextField
          label="Your name"
          autoComplete="name"
          required
          disabled={emailCode && stage === 'code'}
          value={name}
          error={fieldError('name')}
          onChange={(e) => setName(e.target.value)}
        />
        {!emailCode && (
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            help="At least 8 characters."
            value={password}
            error={fieldError('password')}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
        {emailCode && stage === 'code' && (
          <TextField
            label="Six-digit code"
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        )}
        <Button type="submit" full size="large" disabled={busy}>
          {verifyJoin.isPending || join.isPending
            ? 'Joining…'
            : requestJoinCode.isPending
              ? 'Sending…'
              : emailCode && stage === 'details'
                ? 'Email me a code'
                : `Join ${workspaceName}`}
        </Button>
        {emailCode && stage === 'code' && (
          <div className="row center gap-3 wrap">
            <button type="button" className="btn btn-text" disabled={busy} onClick={() => requestJoinCode.mutate()}>
              Resend code
            </button>
          </div>
        )}
      </form>
    </AuthShell>
  )
}
